import { catchError, map, of, take } from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';

import { BrandingService } from '@app/services/branding.service';
import { GuestSessionService } from '@app/services/guest-session.service';
import { Language } from '@app/shared/models/app.model';
import { LanguageSettingsService } from '@app/services/language-settings.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SettingsService } from '@app/services/settings.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { TtsAudioService } from '@app/services/tts-audio.service';

/**
 * Whether read-along can speak this language, per the proxy's `tts_available`
 * on guest.translations.supported_languages. The server owns the answer, so a
 * language that arrived without the flag, from the static fallback list or from
 * a proxy that predates it, is treated as speakable: the proxy refuses the rest
 * on subscribe regardless, and the old behaviour was to offer everything.
 */
function canSpeak(lang: Language | null | undefined): boolean {
  return lang?.ttsAvailable !== false;
}

@Component({
  selector: 'app-qr-language-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslocoPipe
  ],
  templateUrl: './qr-language-dialog.component.html',
  styleUrls: ['./qr-language-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QrLanguageDialogComponent implements OnInit {
  private readonly languageButtons = viewChildren<ElementRef<HTMLButtonElement>>('languageButton');
  private readonly dialogRef = inject<MatDialogRef<QrLanguageDialogComponent>>(MatDialogRef);
  public readonly languageSettings = inject(LanguageSettingsService);
  private readonly qrUserService = inject(GuestSessionService);
  public readonly ttsAudioService = inject(TtsAudioService);
  private readonly settingsService = inject(SettingsService);
  private readonly brandingService = inject(BrandingService);
  private readonly destroyRef = inject(DestroyRef);

  /** SpeakSee logo swaps with theme. */
  readonly isDarkMode = toSignal(this.settingsService.darkMode$, { initialValue: false });

  /** Custom tenant branding logo for the current guest device, if any (else the
   * themed SpeakSee default is shown). Avoids importing BrandingLogosComponent,
   * which would create a circular import via GuestSessionService. */
  readonly tenantLogoUrl = toSignal(
    (() => {
      const uuid = this.qrUserService.getDeviceUuid();
      if (!uuid) return of<string | null>(null);
      return this.brandingService.getBranding(uuid).pipe(
        map((b) => b?.tenant_logo_qr?.url ?? null),
        catchError(() => of<string | null>(null)),
      );
    })(),
    { initialValue: null as string | null },
  );

  selectedLanguage = signal<Language | null>(null);
  /** Local TTS read-along toggle. Off by default; persisted to TtsAudioService on confirm. */
  readAlongEnabled = signal(false);
  /** Onboarding step: 1 = listen/read mode, 2 = language picker. */
  step = signal<1 | 2>(1);

  private readonly dialogData =
    inject(MAT_DIALOG_DATA, { optional: true }) as
      { savedLanguage?: Language | null; ttsEnabled?: boolean } | null;

  /** Saved language preference passed in by the service, if any. */
  private readonly savedLanguage: Language | null = this.dialogData?.savedLanguage ?? null;

  /** TTS rollout flag. When false the dialog is the pre-TTS single-step
   * language picker: no listen/read step, no Back button. */
  protected readonly ttsEnabled: boolean = this.dialogData?.ttsEnabled ?? true;

  languages = signal<Language[]>([]);

  /**
   * The languages the picker actually offers. A guest who chose to listen is
   * not shown regions the proxy told us it cannot speak, because it refuses to
   * subscribe to those; a guest who chose to read sees everything, since
   * translation is unaffected by which voices exist.
   */
  visibleLanguages = computed(() =>
    this.readAlongEnabled()
      ? this.languages().filter((lang) => canSpeak(lang))
      : this.languages()
  );
  verifiedLanguages = computed(() =>
    this.visibleLanguages().filter((lang) => lang.isVerified)
  );
  unverifiedLanguages = computed(() =>
    this.visibleLanguages().filter((lang) => !lang.isVerified)
  );

  constructor() {
    effect(() => {
      const selected = this.selectedLanguage();
      const buttons = this.languageButtons();
      
      if (selected && buttons.length > 0) {
        this.scrollToSelectedLanguage();
      }
    });
  }

  ngOnInit(): void {
    this.dialogRef.disableClose = true;

    // Outside the TTS rollout there is no listen/read choice — go straight
    // to the language picker (readAlongEnabled stays false, so the title and
    // confirm path behave exactly like the pre-TTS dialog).
    if (!this.ttsEnabled) this.step.set(2);

    // Initialize languages from GuestSessionService or fallback to LanguageSettingsService
    const supported = this.qrUserService.supportedLanguages();
    if (supported.length > 0) {
      this.languages.set(supported);
    } else {
      this.languages.set(this.languageSettings.getAllLanguages());
    }

    this.updateSelectedLanguage();
  }

  private updateSelectedLanguage(): void {
    const detectedLanguage = this.languageSettings.detectDeviceLanguage();
    const currentSelected = this.selectedLanguage();
    
    let targetLang = currentSelected || detectedLanguage;
    
    if (targetLang) {
      const match = this.languages().find(l => l.languageCode === targetLang!.languageCode);
      if (match) {
        this.selectedLanguage.set(match);
      } else if (!currentSelected && detectedLanguage) {
         this.selectedLanguage.set(detectedLanguage);
      }
    }
  }

  /**
   * User selects a language - update the app language but don't close the dialog
   */
  selectTranslationLanguage(language: Language): void {
    this.selectedLanguage.set(language);
    this.languageSettings.selectTranslationLanguage(language);
  }

  /**
   * Step 1 mode selection. If a saved language exists (returning user), skips
   * step 2 by saving the language, applying TTS, and closing. The iOS audio
   * gesture requires `setEnabled` to run inside this click handler.
   */
  selectMode(mode: 'listen' | 'read'): void {
    const readAlong = mode === 'listen';
    this.readAlongEnabled.set(readAlong);

    // A language detected from the device, or remembered from a previous
    // visit, may be one the proxy cannot speak. Choosing to listen retires it
    // and sends the guest to the picker, which no longer offers it.
    if (readAlong && !canSpeak(this.selectedLanguage())) {
      this.selectedLanguage.set(null);
    }
    if (readAlong && !canSpeak(this.savedLanguage)) {
      this.step.set(2);
      return;
    }

    if (this.savedLanguage) {
      // TtsAudioService.start() reads languageSettings.seletecedLanguage() to
      // subscribe to the right audio stream. Set it before setEnabled
      // (saveQrUserLanguage is called with skipLanguageSelection=true to
      // match step-2 confirm).
      this.languageSettings.selectTranslationLanguage(this.savedLanguage);
      // Synchronously inside the click gesture — a subscription callback runs
      // after the async save, where iOS blocks AudioContext/audio.play().
      this.ttsAudioService.setEnabled(readAlong);
      const close = () => this.dialogRef.close(this.savedLanguage!);
      this.qrUserService
        .saveQrUserLanguage(this.savedLanguage, true)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: close,
          error: (err) => {
            console.error('Error saving language preference:', err);
            close();
          },
        });
      return;
    }

    this.step.set(2);
  }

  /** Step 2 back button. */
  goBack(): void {
    this.step.set(1);
  }

  /**
   * Confirm the selected language, save it for all microphones, and close the dialog
   */
  confirmLanguageSelection(): void {
    const selected = this.selectedLanguage();
    if (!selected) return;

    // Apply the read-along toggle synchronously inside the user gesture so
    // the AudioContext is allowed to start playing — a subscription callback
    // runs after the async save, outside the gesture's activation window.
    this.ttsAudioService.setEnabled(this.readAlongEnabled());

    // Skip language selection since selectTranslationLanguage was already called
    const close = () => this.dialogRef.close(selected);
    this.qrUserService
      .saveQrUserLanguage(selected, true)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: close,
        error: (error) => {
          console.error('Error saving language preference:', error);
          close();
        },
      });
  }

  /**
   * Scrolls to the selected language button if one is selected
   */
  private scrollToSelectedLanguage(): void {
    const selected = this.selectedLanguage();
    const buttons = this.languageButtons();

    if (!selected || buttons.length === 0) return;

    const selectedButton = buttons.find((buttonRef) => {
      return buttonRef.nativeElement?.getAttribute('data-language-code') === selected.languageCode;
    });

    if (selectedButton) {
      const element = selectedButton.nativeElement;

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });

      element.focus({ preventScroll: true });
    }
  }
}
